// ARIA tree (File tab): folders with the engine's progress per folder, keyboard navigation (↑ ↓ → ← Home End Enter).
import { useMemo, useRef, useState, type ReactNode } from "react";

export interface TreeNode { id: string; parent: string | null; label: ReactNode; textLabel: string; badge?: ReactNode; leaf?: boolean }

export function Tree({ nodes, selected, onSelect, label, initiallyOpen = [] }: { nodes: TreeNode[]; selected?: string; onSelect: (id: string) => void; label: string; initiallyOpen?: string[] }) {
  const [open, setOpen] = useState<Set<string>>(() => new Set(initiallyOpen));
  const [focus, setFocus] = useState<string | undefined>(selected);
  const refs = useRef(new Map<string, HTMLLIElement>());
  const kids = useMemo(() => {
    const m = new Map<string | null, TreeNode[]>();
    const ids = new Set(nodes.map((n) => n.id));
    for (const n of nodes) { const p = n.parent && ids.has(n.parent) ? n.parent : null; m.set(p, [...(m.get(p) ?? []), n]); }
    return m;
  }, [nodes]);
  const visible: TreeNode[] = [];
  const walk = (p: string | null) => { for (const n of kids.get(p) ?? []) { visible.push(n); if (open.has(n.id)) walk(n.id); } };
  walk(null);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const current = focus && visible.some((v) => v.id === focus) ? focus : visible[0]?.id;
  const go = (id: string | undefined) => { if (!id) return; setFocus(id); refs.current.get(id)?.focus(); };
  const toggle = (id: string, to?: boolean) => setOpen((o) => { const s = new Set(o); (to ?? !s.has(id)) ? s.add(id) : s.delete(id); return s; });

  const render = (p: string | null, level: number): ReactNode => (kids.get(p) ?? []).map((n) => {
    const hasKids = (kids.get(n.id) ?? []).length > 0;
    const isOpen = open.has(n.id);
    return (
      <li key={n.id} ref={(el) => { if (el) refs.current.set(n.id, el); }} role="treeitem" aria-level={level} aria-selected={n.id === selected}
        aria-expanded={hasKids ? isOpen : undefined} tabIndex={n.id === current ? 0 : -1} aria-label={n.textLabel}
        onFocus={(e) => { if (e.target === e.currentTarget) setFocus(n.id); }}
        onClick={(e) => { e.stopPropagation(); onSelect(n.id); if (hasKids) toggle(n.id); setFocus(n.id); }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          const i = visible.findIndex((v) => v.id === n.id);
          if (e.key === "ArrowDown") { e.preventDefault(); go(visible[i + 1]?.id); }
          else if (e.key === "ArrowUp") { e.preventDefault(); go(visible[i - 1]?.id); }
          else if (e.key === "ArrowRight") { e.preventDefault(); if (hasKids && !isOpen) toggle(n.id, true); else if (hasKids) go(kids.get(n.id)![0].id); }
          else if (e.key === "ArrowLeft") { e.preventDefault(); if (hasKids && isOpen) toggle(n.id, false); else if (n.parent && byId.has(n.parent)) go(n.parent); }
          else if (e.key === "Home") { e.preventDefault(); go(visible[0]?.id); }
          else if (e.key === "End") { e.preventDefault(); go(visible[visible.length - 1]?.id); }
          else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(n.id); if (hasKids) toggle(n.id); }
        }}
        className="outline-none focus-visible:[&>div]:ring-2 focus-visible:[&>div]:ring-focus">
        <div className={`flex cursor-pointer items-center gap-1.5 rounded px-1 py-1 text-sm ${n.id === selected ? "bg-brand/10 font-medium text-brand" : "hover:bg-sunken"}`} style={{ paddingLeft: 4 + (level - 1) * 14 }}>
          <span aria-hidden="true" className="w-3 text-center text-[10px] text-muted">{hasKids ? (isOpen ? "▾" : "▸") : ""}</span>
          <span className="min-w-0 flex-1 truncate">{n.label}</span>
          {n.badge}
        </div>
        {hasKids && isOpen && <ul role="group">{render(n.id, level + 1)}</ul>}
      </li>
    );
  });
  return <ul role="tree" aria-label={label} className="text-sm">{render(null, 1)}</ul>;
}
